import React, { useEffect } from 'react';
import store, { searchActions, loanActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import RentalOutstandingTable from './tables/RentalOutstandingTable';
import SubNoteOutstandingTable from './tables/SubNoteOutstandingTable';
import MasterNotesOutstandingTable from './tables/MasterNotesOutstandingTable';
import OutstandingLandTable from './tables/OutstandingLandTable';
import OutstandingModelTable from './tables/OutstandingModelTable';
import OutstandingFixFlipTable from './tables/OutstandingFixFlipTable';
import OutstandingBuildRentTable from './tables/OutstandingBuildRentTable';
import OutstandingConstructionTable from './tables/OutstandingConstructionTable';
import { LoanPageType, LoanStatusType, LoanPageOptions } from './common/LoanConstants';
import { getStatusTypeName, getLoanTypeForPageType } from './common/LoanFunctions';
import { LoanModals, SummaryTotals } from './common/LoanComponents';
import { Accordion, BfiCustomStore, HelpText } from '../common/SharedComponents';
import debounce from 'lodash.debounce';
import { LoanApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';
import { BorrowerModals } from '../borrowers/common/BorrowerComponents';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import OutstandingBridgeTable from './tables/OutstandingBridgeTable';

//Used for Loans in Outstanding Status
function LoansLandingPageOutstanding({ pageType = LoanPageType.Landing }) {
    const searchState = useSelector((state) => state.searchState);
    const { dispatch } = store;
    const loanApi = new LoanApi.LoanApi(applicationInsights);

    //const standardLoanGridStore = new BfiCustomStore();
    const constGridStore0 = new BfiCustomStore();
    const constGridStore1 = new BfiCustomStore();
    const constGridStore2 = new BfiCustomStore();
    const constRentalGridStore0 = new BfiCustomStore();
    const constRentalGridStore0b = new BfiCustomStore();
    const constRentalGridStore1 = new BfiCustomStore();
    const constRentalGridStore1b = new BfiCustomStore();
    const constRentalGridStore2 = new BfiCustomStore();
    const constRentalGridStore2b = new BfiCustomStore();
    const constRentalGridStore3 = new BfiCustomStore();
    const constRentalGridStore3b = new BfiCustomStore();

    const debouncedSearch = debounce(dispatch, 500);

    const _onSearchChange = (event) => {
        debouncedSearch(
            searchActions.setSearchText({
                searchText: {
                    ...searchState.searchText,
                    loan: event.target.value,
                },
            })
        );
    };

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, []);

    const _export = () => {
        const loanTypeId = getLoanTypeForPageType(pageType);
        loanApi.exportLoans(loanTypeId, LoanStatusType.Outstanding);
    };

    const _exportSubNotes = () => {
        loanApi.exportSubNotes(LoanStatusType.Outstanding);
    };

    const _exportMasterNotes = () => {
        loanApi.exportMasterNotes(LoanStatusType.Outstanding);
    };

    const _exportRentalLoans = () => {
        loanApi.exportRentalLoans(LoanStatusType.Outstanding);
    };

    const addLoan = (pageType) => {
        if (pageType === LoanPageType.LOC) {
            dispatch(
                loanActions.setEditMasterNoteModal({
                    editMasterNoteModal: {
                        isOpen: true,
                        loanId: 0,
                        showInsurance: false,
                    },
                })
            );
        } else {
            dispatch(
                loanActions.setEditStdLoanModal({
                    editStdLoanModal: {
                        isOpen: true,
                        loanId: 0,
                        showInsurance: false,
                        loanType: getLoanTypeForPageType(pageType),
                    },
                })
            );
        }
    };

    if (pageType === LoanPageType.Landing) {
        return <span>PageType Landing not supported by LoansLandingPageOutstanding</span>;
    }

    return (
        <>
            <div
                className='page-header'
                role='banner'
            >
                <h1>
                    {getStatusTypeName(LoanStatusType.Outstanding)}
                    <span>
                        &nbsp;&nbsp;-
                        <b>&nbsp;&nbsp;{LoanPageOptions[pageType].name}</b>
                    </span>
                </h1>
                <div className='primary-actions'>
                    <button
                        className='hollow'
                        onClick={() => addLoan(pageType)}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        {pageType === LoanPageType.LOC ? 'Add Master Note' : 'Add Loan'}
                    </button>
                    &nbsp;
                    {pageType !== LoanPageType.LOC && pageType !== LoanPageType.Rental && (
                        <button
                            className='hollow'
                            onClick={() => _export()}
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            &nbsp;&nbsp;Export
                        </button>
                    )}
                    {pageType === LoanPageType.Rental && (
                        <button
                            className='hollow'
                            onClick={() => _exportRentalLoans()}
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            &nbsp;&nbsp;Export
                        </button>
                    )}
                    {pageType === LoanPageType.LOC && (
                        <button
                            className='hollow'
                            onClick={() => _exportMasterNotes()}
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            &nbsp;&nbsp;Export Master Notes
                        </button>
                    )}
                    &nbsp;
                    {pageType === LoanPageType.LOC && (
                        <button
                            className='hollow'
                            onClick={() => _exportSubNotes()}
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            &nbsp;&nbsp;Export Sub Notes
                        </button>
                    )}
                    &nbsp;
                    <HelpText />
                </div>
            </div>
            <div
                className='main-content'
                style={{ padding: 0, backgroundColor: '#f4f4f4' }}
            >
                <div className='input-has-icon main-search'>
                    <input
                        type='search'
                        placeholder='Search on BFI Number, Loan Number, City, State, Borrower or User...'
                        defaultValue={searchState?.searchText?.loan}
                        onChange={(event) => _onSearchChange(event)}
                    />
                    <FontAwesomeIcon
                        icon={faSearch}
                        aria-hidden='true'
                        className='icon'
                    />
                </div>
                <SummaryTotals pageType={pageType} />

                <div
                    className='scrollable-content'
                    style={{ padding: '1rem' }}
                >
                    <div
                        className='loans accordion'
                        data-accordion=''
                        data-allow-all-closed='true'
                        data-multi-expand='true'
                    >
                        {pageType === LoanPageType.Rental && (
                            <>
                                <Accordion
                                    title='5 + 2 Year'
                                    isExpanded={true}
                                    //Set the id so that the accordian resets state when the user moves to a different Loan Page
                                    id={`Loan_Rental_Outstanding_60`}
                                >
                                    <div className='card'>
                                        <div className='card-section'>
                                            <RentalOutstandingTable
                                                term={60}
                                                gridStore={constRentalGridStore0}
                                            />
                                            <div style={{ padding: '10px' }}>Loans with Money in Holdback</div>
                                            <RentalOutstandingTable
                                                term={60}
                                                gridStore={constRentalGridStore0b}
                                                hasHoldback={true}
                                            />
                                        </div>
                                    </div>
                                </Accordion>

                                <Accordion
                                    title='10 + 2 Year'
                                    isExpanded={true}
                                    //Set the id so that the accordian resets state when the user moves to a different Loan Page
                                    id={`Loan_Rental_Outstanding_120`}
                                >
                                    <div className='card'>
                                        <div className='card-section'>
                                            <RentalOutstandingTable
                                                term={120}
                                                gridStore={constRentalGridStore1}
                                            />
                                            <div style={{ padding: '10px' }}>Loans with Money in Holdback</div>
                                            <RentalOutstandingTable
                                                term={120}
                                                gridStore={constRentalGridStore1b}
                                                hasHoldback={true}
                                            />
                                        </div>
                                    </div>
                                </Accordion>

                                <Accordion
                                    title='15 + 2 Year'
                                    isExpanded={true}
                                    //Set the id so that the accordian resets state when the user moves to a different Loan Page
                                    id={`Loan_Rental_Outstanding_180`}
                                >
                                    <div className='card'>
                                        <div className='card-section'>
                                            <RentalOutstandingTable
                                                term={180}
                                                gridStore={constRentalGridStore2}
                                            />
                                            <div style={{ padding: '10px' }}>Loans with Money in Holdback</div>
                                            <RentalOutstandingTable
                                                term={180}
                                                gridStore={constRentalGridStore2b}
                                                hasHoldback={true}
                                            />
                                        </div>
                                    </div>
                                </Accordion>
                                <Accordion
                                    title='No Term Set'
                                    isExpanded={false}
                                    //Set the id so that the accordian resets state when the user moves to a different Loan Page
                                    id={`Loan_Rental_Outstanding_notset`}
                                >
                                    <div className='card'>
                                        <div className='card-section'>
                                            <RentalOutstandingTable
                                                term={-1}
                                                gridStore={constRentalGridStore3}
                                            />
                                            <div style={{ padding: '10px' }}>Loans with Money in Holdback</div>
                                            <RentalOutstandingTable
                                                term={-1}
                                                gridStore={constRentalGridStore3b}
                                                hasHoldback={true}
                                            />
                                        </div>
                                    </div>
                                </Accordion>
                            </>
                        )}

                        {pageType !== LoanPageType.LOC && pageType !== LoanPageType.Rental && (
                            <>
                                <Accordion
                                    title='Initial Period'
                                    isExpanded={true}
                                    id={'Acc_Ext0'}
                                >
                                    <div className='card'>
                                        <div className='card-section'>
                                            {pageType === LoanPageType.Construction && (
                                                <OutstandingConstructionTable
                                                    gridStore={constGridStore0}
                                                    extensionNumber={0}
                                                />
                                            )}
                                            {pageType === LoanPageType.BuildRent && (
                                                <OutstandingBuildRentTable
                                                    gridStore={constGridStore0}
                                                    extensionNumber={0}
                                                />
                                            )}
                                            {pageType === LoanPageType.FixFlip && (
                                                <OutstandingFixFlipTable
                                                    gridStore={constGridStore0}
                                                    extensionNumber={0}
                                                />
                                            )}
                                            {pageType === LoanPageType.Land && (
                                                <OutstandingLandTable
                                                    gridStore={constGridStore0}
                                                    extensionNumber={0}
                                                />
                                            )}
                                            {pageType === LoanPageType.Model && (
                                                <OutstandingModelTable
                                                    gridStore={constGridStore0}
                                                    extensionNumber={0}
                                                />
                                            )}
                                            {pageType == LoanPageType.Bridge && (
                                                <OutstandingBridgeTable
                                                    gridStore={constGridStore0}
                                                    extensionNumber={0}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </Accordion>
                                <Accordion
                                    title='Extension 1'
                                    isExpanded={true}
                                    id={'Acc_Ext1'}
                                >
                                    <div className='card'>
                                        <div className='card-section'>
                                            {pageType === LoanPageType.Construction && (
                                                <OutstandingConstructionTable
                                                    gridStore={constGridStore1}
                                                    extensionNumber={1}
                                                />
                                            )}
                                            {pageType === LoanPageType.BuildRent && (
                                                <OutstandingBuildRentTable
                                                    gridStore={constGridStore1}
                                                    extensionNumber={1}
                                                />
                                            )}
                                            {pageType === LoanPageType.FixFlip && (
                                                <OutstandingFixFlipTable
                                                    gridStore={constGridStore1}
                                                    extensionNumber={1}
                                                />
                                            )}
                                            {pageType === LoanPageType.Land && (
                                                <OutstandingLandTable
                                                    gridStore={constGridStore1}
                                                    extensionNumber={1}
                                                />
                                            )}
                                            {pageType === LoanPageType.Model && (
                                                <OutstandingModelTable
                                                    gridStore={constGridStore1}
                                                    extensionNumber={1}
                                                />
                                            )}
                                            {pageType === LoanPageType.Bridge && (
                                                <OutstandingBridgeTable
                                                    gridStore={constGridStore1}
                                                    extensionNumber={1}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </Accordion>
                                <Accordion
                                    title='Extension 2+'
                                    isExpanded={true}
                                    id={'Acc_Ext2'}
                                >
                                    <div className='card'>
                                        <div className='card-section'>
                                            {pageType === LoanPageType.Construction && (
                                                <OutstandingConstructionTable
                                                    gridStore={constGridStore2}
                                                    extensionNumber={2}
                                                />
                                            )}
                                            {pageType === LoanPageType.BuildRent && (
                                                <OutstandingBuildRentTable
                                                    gridStore={constGridStore2}
                                                    extensionNumber={2}
                                                />
                                            )}
                                            {pageType === LoanPageType.FixFlip && (
                                                <OutstandingFixFlipTable
                                                    gridStore={constGridStore2}
                                                    extensionNumber={2}
                                                />
                                            )}
                                            {pageType === LoanPageType.Land && (
                                                <OutstandingLandTable
                                                    gridStore={constGridStore2}
                                                    extensionNumber={2}
                                                />
                                            )}
                                            {pageType === LoanPageType.Model && (
                                                <OutstandingModelTable
                                                    gridStore={constGridStore2}
                                                    extensionNumber={2}
                                                />
                                            )}
                                            {pageType === LoanPageType.Bridge && (
                                                <OutstandingBridgeTable
                                                    gridStore={constGridStore2}
                                                    extensionNumber={2}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </Accordion>
                            </>
                        )}

                        {pageType === LoanPageType.LOC && (
                            <>
                                <Accordion
                                    title={'Master Notes'}
                                    isExpanded={true}
                                    //Set the id so that the accordian resets state when the user moves to a different Loan Page
                                    id={`Loan_Outstanding_MN`}
                                >
                                    <div className='card'>
                                        <div className='card-section'>
                                            <MasterNotesOutstandingTable gridStore={constGridStore0} />
                                        </div>
                                    </div>
                                </Accordion>
                                <Accordion
                                    title={'Sub Notes'}
                                    isExpanded={true}
                                    //Set the id so that the accordian resets state when the user moves to a different Loan Page
                                    id={`Loan_Outstanding_SN`}
                                >
                                    <div className='card'>
                                        <div className='card-section'>
                                            <SubNoteOutstandingTable gridStore={constGridStore1} />
                                        </div>
                                    </div>
                                </Accordion>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <LoanModals />
            <BorrowerModals />
        </>
    );
}

export default LoansLandingPageOutstanding;
